import { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import {
  ProcessingTrackerState,
  ACTIVE_ANALYSIS_STATUSES,
  ACTIVE_JOB_SEARCH_STATUSES,
  COMPLETED_ANALYSIS_STATUSES,
  COMPLETED_JOB_SEARCH_STATUSES,
  ProcessingStatus,
  ActiveProcessItem,
} from '../types/processing.types';

export const useProcessingTracker = (): ProcessingTrackerState => {
  const [state, setState] = useState<ProcessingTrackerState>({
    analysisCount: 0,
    jobSearchCount: 0,
    hasCompletedAnalysis: false,
    hasCompletedJobSearch: false,
    hasActiveProcesses: false,
    justCompletedAnalysis: false,
    justCompletedJobSearch: false,
    activeItems: [],
  });

  // Refs to store items from different sources
  const standardAnalysisItems = useRef<ActiveProcessItem[]>([]);
  const enhancedAnalysisItems = useRef<ActiveProcessItem[]>([]);
  const jobSearchItems = useRef<ActiveProcessItem[]>([]);

  // Refs to store timeouts for cleanup (memory leak fix)
  const analysisTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const jobSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for completion status
  const standardAnalysisCompleted = useRef<boolean>(false);
  const enhancedAnalysisCompleted = useRef<boolean>(false);
  const jobSearchCompleted = useRef<boolean>(false);

  // Previous states for transition detection
  const prevAnalysisActive = useRef<boolean>(false);
  const prevJobSearchActive = useRef<boolean>(false);
  const prevAnalysisCompleted = useRef<boolean>(false);
  const prevJobSearchCompleted = useRef<boolean>(false);

  // Ref to store unsubscribe functions to prevent multiple listeners
  const unsubscribesRef = useRef<Array<() => void>>([]);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    const emptyState: ProcessingTrackerState = {
      analysisCount: 0,
      jobSearchCount: 0,
      hasCompletedAnalysis: false,
      hasCompletedJobSearch: false,
      hasActiveProcesses: false,
      justCompletedAnalysis: false,
      justCompletedJobSearch: false,
      activeItems: [],
    };

    const cleanupListeners = () => {
      unsubscribesRef.current.forEach(unsubscribe => {
        try {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        } catch {
          // Ignore cleanup errors if Firestore is already closed
        }
      });
      unsubscribesRef.current = [];
    };

    // Listen to auth state changes to proactively tear down Firestore
    // listeners BEFORE the Firestore client is terminated on sign-out.
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // Always clean up previous Firestore listeners first
      cleanupListeners();

      if (!user) {
        if (isMountedRef.current) {
          setState(emptyState);
        }
        return;
      }

      setupListeners(user.uid);
    });

    function setupListeners(userId: string) {
      const unsubscribes: Array<() => void> = [];

      const updateGlobalState = () => {
      if (!isMountedRef.current) return;

      const allAnalysisItems = [...standardAnalysisItems.current, ...enhancedAnalysisItems.current];
      const allSearchItems = [...jobSearchItems.current];
      const allActiveItems = [...allAnalysisItems, ...allSearchItems];

      const hasActiveAnalysis = allAnalysisItems.length > 0;
      const hasActiveSearch = allSearchItems.length > 0;
      const hasActive = hasActiveAnalysis || hasActiveSearch;

      const hasCompletedAnalysis = standardAnalysisCompleted.current || enhancedAnalysisCompleted.current;
      const hasCompletedSearch = jobSearchCompleted.current;

      // Detect transitions
      const wasAnalysisActive = prevAnalysisActive.current;
      const justCompletedAnalysis = wasAnalysisActive && !hasActiveAnalysis && hasCompletedAnalysis;

      const wasSearchActive = prevJobSearchActive.current;
      const justCompletedJobSearch = wasSearchActive && !hasActiveSearch && hasCompletedSearch;

      setState(prev => ({
        ...prev,
        analysisCount: allAnalysisItems.length,
        jobSearchCount: allSearchItems.length,
        hasActiveProcesses: hasActive,
        hasCompletedAnalysis,
        hasCompletedJobSearch: hasCompletedSearch,
        justCompletedAnalysis: justCompletedAnalysis || prev.justCompletedAnalysis, // Keep true if already true (handled by timeout)
        justCompletedJobSearch: justCompletedJobSearch || prev.justCompletedJobSearch,
        activeItems: allActiveItems,
      }));

      prevAnalysisActive.current = hasActiveAnalysis;
      prevJobSearchActive.current = hasActiveSearch;
      prevAnalysisCompleted.current = hasCompletedAnalysis;
      prevJobSearchCompleted.current = hasCompletedSearch;

      if (justCompletedAnalysis) {
        // Clear existing timeout to prevent memory leaks
        if (analysisTimeoutRef.current) {
          clearTimeout(analysisTimeoutRef.current);
        }
        analysisTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setState(prev => ({ ...prev, justCompletedAnalysis: false }));
          }
          analysisTimeoutRef.current = null;
        }, 500);
      }
      if (justCompletedJobSearch) {
        // Clear existing timeout to prevent memory leaks
        if (jobSearchTimeoutRef.current) {
          clearTimeout(jobSearchTimeoutRef.current);
        }
        jobSearchTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setState(prev => ({ ...prev, justCompletedJobSearch: false }));
          }
          jobSearchTimeoutRef.current = null;
        }, 500);
      }
    };

    // 1. Standard Analysis
    const analysisRef = doc(db, 'jobAnalysis', userId);
    unsubscribes.push(onSnapshot(
      analysisRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const status = (data?.status as ProcessingStatus) || 'idle';
          if (ACTIVE_ANALYSIS_STATUSES.includes(status)) {
            standardAnalysisItems.current = [{
              id: docSnap.id,
              type: 'analysis',
              name: data.jobTitle || data.companyName || 'Analyse en cours',
            }];
          } else {
            standardAnalysisItems.current = [];
          }
          standardAnalysisCompleted.current = COMPLETED_ANALYSIS_STATUSES.includes(status);
        } else {
          standardAnalysisItems.current = [];
          standardAnalysisCompleted.current = false;
        }
        updateGlobalState();
      },
      () => {
        // Silently ignore listener errors
      }
    ));

    // 2. Enhanced Analysis Active
    const enhancedRef = collection(db, 'jobAnalysisEnhanced');
    const enhancedActiveQuery = query(enhancedRef, where('userId', '==', userId), where('status', 'in', ACTIVE_ANALYSIS_STATUSES), limit(20));
    unsubscribes.push(onSnapshot(
      enhancedActiveQuery,
      (snapshot) => {
        enhancedAnalysisItems.current = snapshot.docs.map(doc => {
          const data = doc.data();
          // Check both direct fields and nested jobInfo for job title
          const jobTitle = data.jobTitle || data.jobInfo?.title || null;
          const companyName = data.companyName || data.jobInfo?.company || null;
          // Build display name with job title and company
          let displayName = 'Analyse détaillée';
          if (jobTitle && companyName) {
            displayName = `${jobTitle} - ${companyName}`;
          } else if (jobTitle) {
            displayName = jobTitle;
          } else if (companyName) {
            displayName = companyName;
          }
          return {
            id: doc.id,
            type: 'analysis',
            name: displayName,
          };
        });
        updateGlobalState();
      },
      () => {
        // Silently ignore listener errors
      }
    ));

    // 3. Enhanced Analysis Completed
    const enhancedCompletedQuery = query(enhancedRef, where('userId', '==', userId), where('status', 'in', COMPLETED_ANALYSIS_STATUSES), limit(1));
    unsubscribes.push(onSnapshot(
      enhancedCompletedQuery,
      (snapshot) => {
        enhancedAnalysisCompleted.current = snapshot.size > 0;
        updateGlobalState();
      },
      () => {
        // Silently ignore listener errors
      }
    ));

    // 4. Job Searches Active
    const searchesRef = collection(db, 'jobSearches');
    const searchesQuery = query(searchesRef, where('userId', '==', userId), where('status', 'in', ACTIVE_JOB_SEARCH_STATUSES), limit(20));
    unsubscribes.push(onSnapshot(
      searchesQuery,
      (snapshot) => {
        jobSearchItems.current = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'job_search',
            name: data.query || data.name || 'Recherche en cours',
          };
        });
        updateGlobalState();
      },
      () => {
        // Silently ignore listener errors
      }
    ));

    // 5. Job Searches Completed
    const completedSearchesQuery = query(searchesRef, where('userId', '==', userId), where('status', 'in', COMPLETED_JOB_SEARCH_STATUSES), limit(1));
    unsubscribes.push(onSnapshot(
      completedSearchesQuery,
      (snapshot) => {
        jobSearchCompleted.current = snapshot.size > 0;
        updateGlobalState();
      },
      () => {
        // Silently ignore listener errors
      }
    ));

    // Store unsubscribe functions in ref
    unsubscribesRef.current = unsubscribes;
    } // end setupListeners

    return () => {
      isMountedRef.current = false;

      // Clear timeouts to prevent memory leaks
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
        analysisTimeoutRef.current = null;
      }
      if (jobSearchTimeoutRef.current) {
        clearTimeout(jobSearchTimeoutRef.current);
        jobSearchTimeoutRef.current = null;
      }

      unsubAuth();
      cleanupListeners();
    };
  }, []);

  return state;
};
